import { yupResolver } from "@hookform/resolvers/yup";
import BreadcrumbWithLink from "components/Common/BreadcrumbWithLink";
import MessageFormValidate from "components/shared-components/MessageFormValidate";
import { formConvertErrorMessage, formValidate } from "helpers/form_helper";
import {
  useCreateUserProfile,
  useListEmailDomainUserProfile,
  useListRoleUserProfile,
} from "hooks";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MetaTags } from "react-meta-tags";
import { Link, useNavigate } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { Button, Card, CardBody, Col, FormGroup, Label, Row } from "reactstrap";
import * as yup from "yup";
import { getPhoneValidation } from "constants/validate";

const CreateUserProfile = props => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(["userProfile"]);
  const { addToast } = useToasts();

  // const isCreate = CheckActionAccess(actionAccess.CREATE_USER);

  const getInitDefaultValues = () => {
    return {
      display_name: "",
      user_name: "",
      email_domain: "",
      email: "",
      phone: "",
      user_role: [],
    };
  };

  const schema = yup.object().shape({
    display_name: yup
      .string()
      .required(t("form.validate.required", { title: t("form.display_name") }))
      .max(250, t("form.validate.char_max", { max: 250 })),
    user_name: yup
      .string()
      .required(t("form.validate.required", { title: t("form.user_name") }))
      .matches(/^[a-z0-9.]*$/, t("form.validate.email_prefix"))
      .max(50, t("form.validate.char_max", { max: 50 })),
    email_domain: yup
      .string()
      .required(t("form.validate.select", { title: t("form.email_domain") })),
    email: yup
      .string()
      .required(t("form.validate.required", { title: t("form.email") }))
      .email(t("form.validate.invalid", { title: t("form.email") }))
      .max(50, t("validate.char_max", { max: 50 })),
    phone: yup
      .string()
      .nullable()
      .matches(getPhoneValidation(t).regex, getPhoneValidation(t).errorMessage)
      .when(getPhoneValidation(t).when),
    user_role: yup.array().min(
      1,
      t("form.validate.select", {
        title: t("form.user_role"),
      })
    ),
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, touchedFields, isSubmitting },
    control,
    setValue,
    getValues,
    setError,
    register,
    unregister,
    trigger,
  } = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: getInitDefaultValues(),
  });

  // List Role User Profile
  const {
    isLoading: isLoadingListRoleUserProfile,
    data: userProfileRoleList,
    refetch: listRoleUserProfile,
  } = useListRoleUserProfile({ params: {} });

  // List Email Domain User Profile
  const onSuccessListEmailDomainUserProfile = data => {
    const { items } = data;
    if (items?.length > 0) {
      setValue("email_domain", `@${items[0]}`, { shouldValidate: true });
    }
  };
  const {
    isLoading: isLoadingListEmailDomainUserProfile,
    data: userProfileEmailDomainList,
    refetch: listEmailDomainUserProfile,
  } = useListEmailDomainUserProfile(
    { params: {} },
    onSuccessListEmailDomainUserProfile
  );

  // Create User Profile
  const onSuccessCreateUserProfile = _data => {
    navigate("/user-profile/list");
  };
  const onErrorCreateUserProfile = error => {
    if (error.code === 422 && error.fields.length > 0) {
      error.fields.forEach(item => {
        let fieldName = item.field;
        switch (item.field) {
          case "common":
            addToast(item.message, {
              appearance: "error",
              autoDismiss: true,
            });
            break;
          default:
            break;
        }
        setError(fieldName, {
          type: "manual",
          message: formConvertErrorMessage(item.message, t),
        });
      });
    }
  };
  const { isLoading: isLoadingCreateUserProfile, mutate: createUserProfile } =
    useCreateUserProfile(onSuccessCreateUserProfile, onErrorCreateUserProfile);

  useEffect(() => {
    listRoleUserProfile();
    listEmailDomainUserProfile();
  }, []);

  const onSubmit = _values => {
    const data = getValues();
    createUserProfile({
      display_name: data.display_name,
      username: String(`${data.user_name}${data.email_domain}`).toLowerCase(),
      roles: data.user_role,
      email: data.email,
      phone: data.phone,
    });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>{t("title.create")}</title>
        </MetaTags>
        <div className="container-fluid">
          <BreadcrumbWithLink
            title={t("title.create")}
            breadcrumbItems={[
              { name: t("common:general_management") },
              { name: t("title.list"), link: "/user-profile/list" },
              { name: t("title.create") },
            ]}
          />

          <Row className="row-cols-1">
            <Col>
              <Card>
                <CardBody>
                  <Row className="row-cols-1 row-cols-md-2">
                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="display_name">
                          {t("form.display_name")}{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <input
                          type="text"
                          className={`form-control ${formValidate(
                            errors,
                            touchedFields,
                            "display_name"
                          )}`}
                          placeholder={t("placeholder.enter", {
                            title: t("form.display_name"),
                          })}
                          {...register("display_name")}
                        />
                        {errors.display_name && (
                          <MessageFormValidate
                            error={errors.display_name.message}
                          />
                        )}
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="email">
                          {t("form.email")}{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <input
                          type="text"
                          className={`form-control ${formValidate(
                            errors,
                            touchedFields,
                            "email"
                          )}`}
                          placeholder={t("placeholder.enter", {
                            title: t("form.email"),
                          })}
                          {...register("email")}
                        />
                        {errors.email && (
                          <MessageFormValidate error={errors.email.message} />
                        )}
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="phone">{t("form.phone")}</Label>
                        <input
                          type="text"
                          className={`form-control ${formValidate(
                            errors,
                            touchedFields,
                            "phone"
                          )}`}
                          placeholder={t("placeholder.enter", {
                            title: t("form.phone"),
                          })}
                          {...register("phone")}
                        />
                        {errors.phone && (
                          <MessageFormValidate error={errors.phone.message} />
                        )}
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup className="mb-3">
                        <Label htmlFor="user_name">
                          {t("form.user_name")}{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <div className="input-group">
                          <input
                            type="text"
                            className={`form-control ${formValidate(
                              errors,
                              touchedFields,
                              "user_name"
                            )}`}
                            placeholder={t("placeholder.enter", {
                              title: t("form.user_name"),
                            })}
                            {...register("user_name")}
                          />

                          <select
                            className={`form-select ${formValidate(
                              errors,
                              touchedFields,
                              "email_domain"
                            )}`}
                            style={{ maxWidth: "250px", fontWeight: 900 }}
                            {...register("email_domain")}
                          >
                            {userProfileEmailDomainList?.items?.length > 0 &&
                              userProfileEmailDomainList.items.map(item => (
                                <option key={item} value={`@${item}`}>
                                  {`@${item}`}
                                </option>
                              ))}
                          </select>
                        </div>
                        {errors.user_name && (
                          <MessageFormValidate
                            error={errors.user_name.message}
                          />
                        )}
                        {errors.email_domain && (
                          <MessageFormValidate
                            error={errors.email_domain.message}
                          />
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className="row-cols-1 mb-3">
                    <Col>
                      <FormGroup>
                        <Label htmlFor="user_role">
                          {t("form.user_role")}{" "}
                          <span className="text-danger">*</span>
                        </Label>
                      </FormGroup>
                    </Col>
                    {userProfileRoleList?.items?.length > 0 && (
                      <Col>
                        <Card className="border-1 border-primary mb-1">
                          <CardBody>
                            <Row className="row-cols-2 row-cols-md-4 row-cols-xl-5">
                              {userProfileRoleList.items
                                .filter(item => item.name !== "DEFAULT")
                                .map(item => {
                                  switch (item.code) {
                                    // case "ROLE_ECONTRACT_FEC_GUEST":
                                    // case "ROLE_ECONTRACT_FEC_EMPLOYEE":
                                    //   break;
                                    default:
                                      return (
                                        <Col key={item.code}>
                                          <FormGroup check>
                                            <Label check>
                                              <input
                                                type="checkbox"
                                                className="form-check-input"
                                                value={item.code}
                                                {...register("user_role")}
                                              />
                                              {item.name}
                                            </Label>
                                          </FormGroup>
                                        </Col>
                                      );
                                  }
                                })}
                            </Row>
                          </CardBody>
                        </Card>
                      </Col>
                    )}
                    <Col>
                      {errors.user_role && (
                        <MessageFormValidate error={errors.user_role.message} />
                      )}
                    </Col>
                  </Row>
                  <Row className="justify-content-end">
                    <Col className="col-auto">
                      <Link
                        to="/user-profile/list"
                        className="btn btn-danger btn-label"
                      >
                        <i className="fas fa-undo label-icon"></i>
                        {t("form.cancel")}
                      </Link>
                    </Col>

                    <Col className="col-auto">
                      <Button
                        color="primary"
                        className="btn-label"
                        type="button"
                        onClick={() => {
                          handleSubmit(onSubmit)();
                        }}
                        disabled={
                          isLoadingCreateUserProfile ||
                          isLoadingListEmailDomainUserProfile ||
                          isLoadingListRoleUserProfile
                        }
                      >
                        <i className="far fa-save fa-lg label-icon"></i>
                        {t("form.save")}
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default CreateUserProfile;
